/**
 * Shared constants and status mappings.
 * From UI_UX_SPEC.md §1.5, §2.2, and FRONTEND_BUILD_SPEC.md §2.2-2.3
 */

export const COUNTRY_META = {
  KE: { name: "Kenya", flag: "🇰🇪", currency: "KES", currencySymbol: "KSh", badgeBg: "bg-brand-light", badgeText: "text-brand" },
  NG: { name: "Nigeria", flag: "🇳🇬", currency: "NGN", currencySymbol: "₦", badgeBg: "bg-warning-light", badgeText: "text-warning" },
  ZA: { name: "South Africa", flag: "🇿🇦", currency: "ZAR", currencySymbol: "R", badgeBg: "bg-info-light", badgeText: "text-info" },
  NA: { name: "Namibia", flag: "🇳🇦", currency: "NAD", currencySymbol: "N$", badgeBg: "bg-purple-100", badgeText: "text-purple-700" },
  ZM: { name: "Zambia", flag: "🇿🇲", currency: "ZMW", currencySymbol: "K", badgeBg: "bg-emerald-100", badgeText: "text-emerald-700" },
  GH: { name: "Ghana", flag: "🇬🇭", currency: "GHS", currencySymbol: "GH₵", badgeBg: "bg-amber-100", badgeText: "text-amber-700" },
} as const;

export const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_META) as Array<keyof typeof COUNTRY_META>;

/** Check if a string is a supported country code. */
export function isSupportedCountry(code: string): code is keyof typeof COUNTRY_META {
  return code in COUNTRY_META;
}

export const LICENSE_STATUS_CONFIG = {
  not_started:              { label: "Not started",     bg: "bg-surface-tertiary", text: "text-text-secondary" },
  preparing:                { label: "Preparing",       bg: "bg-warning-light",   text: "text-warning" },
  submitted:                { label: "Submitted",       bg: "bg-info-light",      text: "text-info" },
  under_review:             { label: "Under review",    bg: "bg-info-light",      text: "text-info" },
  additional_info_requested:{ label: "Info requested",  bg: "bg-warning-light",   text: "text-warning" },
  approved:                 { label: "Approved",        bg: "bg-brand-light",     text: "text-brand" },
  rejected:                 { label: "Rejected",        bg: "bg-danger-light",    text: "text-danger" },
  expired:                  { label: "Expired",         bg: "bg-danger-light",    text: "text-danger" },
  blocked:                  { label: "Blocked",         bg: "bg-danger-light",    text: "text-danger" },
  unrecognized:             { label: "Unrecognized",    bg: "bg-surface-tertiary", text: "text-text-tertiary" },
} as const;

export const COMPLIANCE_STATUS_CONFIG = {
  not_assessed:   { label: "Not assessed",   bg: "bg-surface-tertiary", text: "text-text-secondary" },
  compliant:      { label: "Compliant",      bg: "bg-brand-light",     text: "text-brand" },
  in_progress:    { label: "In progress",    bg: "bg-info-light",      text: "text-info" },
  non_compliant:  { label: "Non-compliant",  bg: "bg-danger-light",    text: "text-danger" },
  not_applicable: { label: "N/A",            bg: "bg-surface-tertiary", text: "text-text-tertiary" },
  exempt:         { label: "Exempt",         bg: "bg-warning-light",   text: "text-warning" },
} as const;

export const GOVERNMENT_TIER_CONFIG = {
  gate:             { label: "Gate",            bg: "bg-danger-light",    text: "text-danger" },
  pre_operational:  { label: "Pre-operational", bg: "bg-warning-light",   text: "text-warning" },
  ongoing:          { label: "Ongoing",         bg: "bg-info-light",      text: "text-info" },
} as const;

export const PROVENANCE_CONFIG = {
  manual:       { label: "Manual",     icon: "✎", bg: "bg-surface-tertiary", text: "text-text-secondary" },
  extracted:    { label: "Extracted",  icon: "✦", bg: "bg-info-light",      text: "text-info" },
  calculated:   { label: "Calculated", icon: "✦",  bg: "bg-ai-light",       text: "text-ai" },
  needs_review: { label: "Review",     icon: "⚠",  bg: "bg-warning-light",  text: "text-warning" },
  empty:        { label: "Missing",    icon: "—",  bg: "bg-danger-light",    text: "text-danger" },
} as const;

export const FIELD_CATEGORIES = [
  { id: "company",       label: "Company" },
  { id: "site",          label: "Site" },
  { id: "plant",         label: "Plant Technical" },
  { id: "financial",     label: "Financial" },
  { id: "environmental", label: "Environmental" },
  { id: "grid",          label: "Grid Connection" },
  { id: "offtake",       label: "Offtake" },
  { id: "community",     label: "Community" },
  { id: "import",        label: "Import & Equipment" },
  { id: "timeline",      label: "Timeline" },
  { id: "regulatory",    label: "Regulatory" },
] as const;

/** Extraction confidence thresholds (from ARCHITECTURE.md §4) */
export const CONFIDENCE = {
  AUTO_COMMIT: 0.80,   // ≥80%: auto-committed
  NEEDS_REVIEW: 0.60,  // 60-79%: needs human review
  LOW: 0.60,           // <60%: low confidence warning
} as const;

/** Capacity routing thresholds in kW (from REGULATORY_SEED_DATA.md §4.3) */
export const CAPACITY_THRESHOLDS = {
  KE_NET_METERING_MAX: 1000,   // ≤1MW: net metering / exempt
  NG_REGISTRATION_MAX: 100,    // ≤100kW: registration only
  NG_MINI_GRID_MAX: 1000,      // ≤1MW: mini-grid permit
  ZA_SSEG_MAX: 100,            // ≤100kW: municipal SSEG registration
  NA_NET_METERING_MAX: 500,    // ≤500kW: ECB net metering
  ZM_LICENSE_EXEMPT_MAX: 250,  // <250kW own-use: no ERB license required (Electricity Act 2019)
  GH_EMBEDDED_MAX: 500,        // ≤500kW: simplified permit (Energy Commission)
} as const;
